import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';

const CALENDLY_URL = 'https://calendly.com/hello-kingmakersinc/discovery';

function RevealSection({ children, className = '', delay = 0 }) {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="about-page" className="px-6 lg:px-8 pt-[80px] pb-[100px] md:pt-[120px] md:pb-[120px]">
      <div className="max-w-4xl mx-auto">
        <RevealSection>
          <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-4">About</p>
          <h1 className="font-inter font-bold text-[38px] md:text-[54px] lg:text-[68px] leading-[1.1] tracking-tight text-km-white">
            We build AI systems and help you deploy them.
          </h1>
        </RevealSection>

        <RevealSection delay={100}>
          <div className="mt-16 p-[30px] md:p-[50px] rounded-[10px] bg-km-card-gradient border border-km-gray-3">
            <h2 className="font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white">
              What King Makers is
            </h2>
            <p className="mt-4 font-inter font-medium text-base md:text-lg text-km-gray-5 leading-[1.3]">
              King Makers is an AI systems and consulting company founded in 2026. We build production AI systems (agents that schedule workforces, diagnose businesses, and do real operational work), and we provide the consulting to deploy them inside your business, not just demo them.
            </p>
          </div>
        </RevealSection>

        <RevealSection delay={150}>
          <div className="mt-[30px] p-[30px] md:p-[50px] rounded-[10px] bg-km-card-gradient border border-km-gray-3">
            <h2 className="font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white">
              How we work
            </h2>
            <p className="mt-4 font-inter font-medium text-base md:text-lg text-km-gray-5 leading-[1.3]">
              The same three-step motion every time: a{' '}
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-km-gold hover:text-km-gold-deep transition-colors cursor-pointer"
              >
                30-minute discovery call
              </a>{' '}
              on your operations, constraints, and where decisions bottleneck; a diagnostic, in which our agent systems analyze your business and produce a consulting-grade assessment; then deployment, where we build or configure the AI system that removes the bottleneck, and stay until it runs.
            </p>
          </div>
        </RevealSection>

        <RevealSection delay={200}>
          <div className="mt-[30px] p-[30px] md:p-[50px] rounded-[10px] bg-km-card-gradient border border-km-gray-3">
            <h2 className="font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white">
              Who is behind it
            </h2>
            <p className="mt-4 font-inter font-medium text-base md:text-lg text-km-gray-5 leading-[1.3]">
              King Makers is founder-led. Before founding King Makers, its founder built a business operations background across retail, consumer goods, and technology sales: the kind of operational work our agent systems are now built to carry.
            </p>
            <p className="mt-4 font-inter font-medium text-sm text-km-gray-5">
              Based in the Greater Toronto Area, Canada.
            </p>
          </div>
        </RevealSection>

        <RevealSection delay={250}>
          <div className="relative overflow-hidden text-center mt-[60px] md:mt-[80px] p-10 md:p-16 rounded-[16px] bg-km-card-gradient border-2 border-km-gold/30 shadow-km-gold-soft">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 -top-[250px] -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-km-gold/10 blur-[100px]"
            />
            <h2 className="relative font-inter font-bold text-[26px] md:text-[38px] lg:text-[46px] leading-[1.1] tracking-tight text-km-white max-w-3xl mx-auto">
              Ready to see what an AI agent could take off your plate?
            </h2>
            <div className="relative mt-10 flex flex-col items-center gap-5">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-discovery-cta"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-[10px] bg-km-gold text-km-gray-1 font-inter font-medium text-sm tracking-wide hover:bg-km-gold-deep transition-colors duration-300 cursor-pointer"
              >
                Book a Discovery Call
              </a>
              <Link
                to="/contact"
                data-testid="about-contact-cta"
                className="font-inter font-medium text-sm text-km-gray-5 hover:text-km-gold transition-colors cursor-pointer"
              >
                Or write to us
              </Link>
            </div>
          </div>
        </RevealSection>
      </div>
    </div>
  );
}
